import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Input from '../../Components/Input/Input';
import Button from '../../Components/Button/Button';
import { handleValidationErrors, validateForm } from '../../Utils/validationHelper';
import { postApi } from '../../Services/apiService';
import { toast } from 'react-toastify';
import {
  clearSession,
  getEmailFromResponse,
  getRoleFromResponse,
  isTenantRole,
  PENDING_LOGIN_KEYS,
} from '../../Utils/postLoginNavigation';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  const asRole = searchParams.get('as');

  // Create refs for all input fields
  const inputRefs = {
    email: useRef(null),
    password: useRef(null),
  };

  useEffect(() => {
    // Add animation classes on mount
    const infoSection = document.querySelector('.login-info');
    const formSection = document.querySelector('.login-form-section');
    const formCard = document.querySelector('.login-form-card');
    
    if (infoSection) infoSection.classList.add('animate-fade-in-left');
    if (formSection) formSection.classList.add('animate-fade-in-right');
    if (formCard) formCard.classList.add('animate-scale-in');
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // Prevent leading spaces for text/email/password inputs
    if (name === 'email' || name === 'password') {
      if (newValue.startsWith(' ')) {
        newValue = newValue.trimStart();
      }
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Define validation rules
    const validationRules = {
      email: {
        required: true,
        pattern: /\S+@\S+\.\S+/,
        message: {
          required: 'Email is required',
          pattern: 'Email is invalid',
        },
      },
      password: {
        required: true,
        message: {
          required: 'Password is required',
        },
      },
    };

    // Validate the form
    const newErrors = validateForm(formData, validationRules);

    // Handle errors (will focus on first error input)
    if (handleValidationErrors(newErrors, inputRefs, setErrors)) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      // Call login API
      const response = await postApi('/auth/login', {
        email: formData.email,
        password: formData.password,
      });

      if (response?.statusCode === 200) {
        // Credentials are valid, but the session does not start here any more:
        // the backend has emailed a 6-digit code that has to be verified first.
        const email = getEmailFromResponse(response) || formData.email;
        const role = getRoleFromResponse(response);

        // If the user arrived from "Start Tenant Screening", stop a landlord
        // account before it burns an OTP.
        if (redirectTo && asRole === 'tenant' && role !== undefined && role !== null && role !== '' && !isTenantRole(role)) {
          const message = 'You are signed in as a landlord. Please sign out and sign up as a tenant to apply for this property.';
          setErrors({ submit: message });
          toast.error('You are signed in as a landlord. Sign out and sign up as a tenant to apply.');
          setIsLoading(false);
          return;
        }

        // Hand the OTP screen everything it needs, without creating a session.
        // Any earlier session goes first: until this login clears its OTP there
        // is no authenticated user, so nothing may unlock a dashboard.
        try {
          clearSession();
          localStorage.setItem(PENDING_LOGIN_KEYS.email, email);
          localStorage.setItem(PENDING_LOGIN_KEYS.response, JSON.stringify(response));
          if (role !== undefined && role !== null && role !== '') {
            localStorage.setItem(PENDING_LOGIN_KEYS.role, String(role));
          }
        } catch (storageError) {
          console.error('Error storing pending login data:', storageError);
        }

        toast.success(response?.message || 'A 6-digit verification code has been sent to your email');

        navigate('/verify-login-otp', {
          state: { email, role, redirect: redirectTo, as: asRole },
        });
      } else {
        const errorMessage = response?.message || 'Login failed. Please check your credentials.';
        setErrors({ submit: errorMessage });
        toast.error(errorMessage);
      }
    } catch (error) {
      // Only show error if we haven't already shown success
      if (!errors.submit) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Login failed. Please try again.';
        setErrors({ submit: errorMessage });
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-info">
          <div className="login-logo">
            <Link to="/" onClick={() => localStorage.clear()}>
              <img src={window.getPublicUrl("/assets/images/Logo.png")} alt="ClearRent" className="login-logo-img" />
            </Link>
          </div>
          <h1 className="login-welcome">Welcome back</h1>
          <p className="login-description">
            Access your dashboard to manage properties and review tenant screenings.
          </p>
        </div>
        <div className="login-form-section">
          <h2 className="login-form-title">Sign in</h2>
          <p className="login-form-subtitle">
            Enter your credentials to access your account
          </p>
          <form onSubmit={handleSubmit} className={`login-form-card ${Object.keys(errors).length > 0 ? 'has-errors' : ''}`} noValidate>
            <div className="login-form-header">Account Information</div>
            <div className={`login-form-fields ${Object.keys(errors).length > 0 ? 'has-errors' : ''}`}>
              <Input
                ref={inputRefs.email}
                type="email"
                name="email"
                label="Email"
                placeholder="Eg. JohnDey4533@gmail.com"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                required
              />
              <div className="login-password-wrapper">
                <div className="login-password-label-row">
                  <label htmlFor="password" className="input-label">
                    Password
                    <span className="required">*</span>
                  </label>
                </div>
                <div className="login-password-input-wrapper">
                  <input
                    ref={inputRefs.password}
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    placeholder="Eg. 451234nnkdk451"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className={`input-field ${errors.password ? 'input-error' : ''}`.trim()}
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <img 
                      src={window.getPublicUrl ? window.getPublicUrl(showPassword ? "/assets/images/Eye.svg" : "/assets/images/eye-closed.svg") : (showPassword ? "/assets/images/Eye.svg" : "/assets/images/eye-closed.svg")} 
                      alt={showPassword ? "Hide password" : "Show password"} 
                      className="login-eye-icon"
                    />
                  </button>
                </div>
                <div className={`error-message-container ${errors.password ? 'has-error' : ''}`}>
                  {errors.password && <span className="error-message">{errors.password}</span>}
                </div>
                <div className="login-forgot-link-wrapper">
                  <Link to="/forgot-password" className="login-forgot-link">
                    Forgot Password
                  </Link>
                </div>
              </div>
            </div>
            {errors.submit && (
              <div className="error-message-container has-error" style={{ marginBottom: '1rem' }}>
                <span className="error-message">{errors.submit}</span>
              </div>
            )}
            <Button type="submit" variant="success" className="login-submit-button" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Continue'}
            </Button>
            <p className="login-signup-link">
              Don't have an account?{' '}
              <Link to={redirectTo && asRole ? `/choose-role?redirect=${encodeURIComponent(redirectTo)}&as=${asRole}` : '/choose-role'}>
                Sign up
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
